import classNames from 'classnames';
import type { CSSProperties } from 'react';
import styled from 'styled-components';

import type { HexColor } from '#src/style/types.ts';

import missions from './assets/missions.webp';
import wave1 from './assets/wave1.webp';
import wave2 from './assets/wave2.webp';

const Root = styled.div`
  position: relative;
  isolation: isolate;
  &.light {
    background:
      linear-gradient(
        0deg,
        rgba(0, 0, 0, var(--gradient-opacity)) 0%,
        rgba(0, 0, 0, var(--gradient-opacity)) 100%
      ),
      var(--color),
      no-repeat center / cover var(--background-image);
    background-blend-mode: normal, overlay, normal;
  }
  &.dark {
    &:before {
      content: '';
      position: absolute;
      inset: 0;
      background: var(--c-surface-background);
    }
    &:after {
      content: '';
      position: absolute;
      inset: 0;
      background: var(--color);
      mix-blend-mode: overlay;
    }
  }
`;

const DarkImg = styled.div`
  background: no-repeat center / cover var(--background-image);
  opacity: var(--image-opacity);
  width: 100%;
  height: 100%;
`;

const Dots = styled.div`
  position: absolute;
  inset: 0;
  background: conic-gradient(
      from 90deg at 3px 3px,
      var(--c-nd-100) 25%,
      var(--c-black) 0
    )
    0 0/5px 5px;
  opacity: 0.08;
`;

const BACKGROUND_IMAGES = {
  wave1,
  wave2,
  missions,
};

type Props = {
  background: 'wave1' | 'wave2' | 'missions';
  mode?: 'light' | 'dark';
  color?: HexColor | [HexColor, HexColor];
  gradientOpacity?: number;
  imageOpacity?: number;
  className?: string;
  withDots?: boolean;
};

export const TextureBackground = ({
  background,
  mode = 'dark',
  imageOpacity = 0.1,
  gradientOpacity = 0.4,
  color,
  className,
  withDots = false,
}: Props) => {
  const getColor = () => {
    if (!color) {
      return 'var(--c-black)';
    }
    if (typeof color === 'string') {
      return `linear-gradient(0deg, ${color} 0%, ${color} 100%)`;
    }
    return `linear-gradient(0deg, ${color[0]} 0%, ${color[1]} 100%)`;
  };
  return (
    <Root
      style={
        {
          '--background-image': `url(${BACKGROUND_IMAGES[background]})`,
          '--color': getColor(),
          '--image-opacity': imageOpacity,
          '--gradient-opacity': gradientOpacity,
        } as CSSProperties
      }
      className={classNames(mode, className)}
    >
      {mode === 'dark' && <DarkImg />}
      {!!withDots && <Dots />}
    </Root>
  );
};
